import { formatCalendarDate } from '@shared/utils'
import type { SortOption, TransactionFilters } from '../types/transactionTypes'

// One mapping from the ledger's filter state to `/wallets/fiat/activity`
// query params, shared by the list and the PDF export so the statement
// always matches the rows on screen. Pagination is the caller's concern —
// the list adds page/limit, the export must not.

export type TransactionQueryParams = Record<string, string | number | boolean>

/** Server default; sending it is noise. */
const DEFAULT_SORT: SortOption = 'newest_first'
/** Lower bound for a custom range whose "from" was left blank. */
const OPEN_RANGE_START = '1970-01-01'
/** `dateFrom` / `dateTo` are ignored by the API unless this accompanies them. */
const CUSTOM_DATE_RANGE = 'custom'

/** A positive number, or null for blank / zero / junk — neither is a filter. */
const toPositiveAmount = (raw: string): number | null => {
  const value = Number(raw)
  return raw.trim() !== '' && Number.isFinite(value) && value > 0 ? value : null
}

export function buildTransactionQueryParams(
  filters: TransactionFilters,
  /** Injected for tests; the user's local calendar date. */
  today: string = formatCalendarDate(),
): TransactionQueryParams {
  const params: TransactionQueryParams = {}

  const search = filters.search.trim()
  if (search) params.search = search
  // "All" is an omitted param — the API has no `all` value.
  if (filters.type) params.type = filters.type
  if (filters.status) params.status = filters.status
  if (filters.sort && filters.sort !== DEFAULT_SORT) params.sort = filters.sort

  // A one-sided range is still a range: fill the open end.
  if (filters.dateFrom || filters.dateTo) {
    params.dateRange = CUSTOM_DATE_RANGE
    params.dateFrom = filters.dateFrom || OPEN_RANGE_START
    params.dateTo = filters.dateTo || today
  }

  const amountMin = toPositiveAmount(filters.amountMin)
  const amountMax = toPositiveAmount(filters.amountMax)
  if (amountMin !== null) params.amountMin = amountMin
  if (amountMax !== null) params.amountMax = amountMax

  return params
}
